import { useEffect, useMemo, useRef, useState } from 'react';
import { useCatalogo, usePedirCatalogo } from '../api/hooks';
import { useSesion } from '../context/Sesion';
import type { ColumnaCatalogo } from '../types/api';
import { fecha } from '../utils/formato';
import { NOMBRE } from '../utils/validacion';

/** «Familia estadística 3» para TSICOD_2; el título tal cual si el campo no tiene dimensión. */
export function tituloColumna(c: ColumnaCatalogo): string {
  const t = c.titulo ?? '';
  return c.dimension > 1 && c.indice !== null ? `${t} ${c.indice + 1}`.trim() : t;
}

export function lista(valor: string): string[] {
  const v = valor.trim();
  return v === '' || v === '*' ? [] : v.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
}

/**
 * Columnas de la tabla con su check y su título de X3. Escribe en `valor` lo mismo que
 * entiende el back: «*» (todas) o la lista separada por comas, en el orden de X3.
 * ROWID y UPDDATTIM_0 van siempre (el motor las necesita): marcadas y sin poder quitarlas.
 */
export function SelectorColumnas({
  tabla,
  valor,
  excluirLob,
  alCambiar,
  error,
}: {
  tabla: string;
  valor: string;
  excluirLob: boolean;
  alCambiar: (v: string) => void;
  error?: string;
}) {
  const nombre = tabla.trim().toUpperCase();
  const valido = NOMBRE.test(nombre);
  const catalogo = useCatalogo(valido ? nombre : null);
  const pedir = usePedirCatalogo(nombre);
  const { puede } = useSesion();
  const [buscar, setBuscar] = useState('');
  const [aMano, setAMano] = useState(false);
  const pedido = useRef<string | null>(null);

  const datos = catalogo.data;
  const columnas = useMemo(() => datos?.columnas ?? [], [datos]);
  const hay = columnas.length > 0;
  const todas = valor.trim() === '' || valor.trim() === '*';
  const elegidas = useMemo(() => new Set(lista(valor)), [valor]);

  // Sin catálogo todavía: se pide a Airflow una vez, sin que el usuario tenga que hacer nada.
  useEffect(() => {
    if (!datos || hay || datos.pendiente || datos.existe === false || !puede('dwh.editor') || pedido.current === nombre) return;
    pedido.current = nombre;
    pedir.mutate();
  }, [datos, hay, nombre, pedir, puede]);

  const disponible = (c: ColumnaCatalogo) => !(excluirLob && c.lob);
  const marcada = (c: ColumnaCatalogo) => c.obligatoria || (disponible(c) && (todas || elegidas.has(c.columna)));

  const escribir = (marcadas: Set<string>) => {
    const cols = columnas.filter((c) => c.obligatoria || (disponible(c) && marcadas.has(c.columna))).map((c) => c.columna);
    // Las que ya no están en X3 se conservan: el back avisará al prepararla.
    const ajenas = [...elegidas].filter((x) => !columnas.some((c) => c.columna === x));
    alCambiar([...cols, ...ajenas].join(','));
  };
  const actuales = () => new Set(columnas.filter(marcada).map((c) => c.columna));

  const alternar = (c: ColumnaCatalogo) => {
    const s = actuales();
    if (s.has(c.columna)) s.delete(c.columna);
    else s.add(c.columna);
    escribir(s);
  };

  const filtro = buscar.trim().toLowerCase();
  const visibles = filtro
    ? columnas.filter((c) => c.columna.toLowerCase().includes(filtro) || tituloColumna(c).toLowerCase().includes(filtro))
    : columnas;
  const marcarVisibles = (si: boolean) => {
    const s = actuales();
    for (const c of visibles) {
      if (c.obligatoria || !disponible(c)) continue;
      if (si) s.add(c.columna);
      else s.delete(c.columna);
    }
    escribir(s);
  };
  const ajenas = [...elegidas].filter((x) => hay && !columnas.some((c) => c.columna === x));
  const nMarcadas = columnas.filter(marcada).length;

  return (
    <fieldset className={`selector-columnas ${error ? 'campo-con-error' : ''}`}>
      <legend>Columnas</legend>

      {!valido && <p className="ayuda">Escribe el nombre de la tabla para ver sus columnas.</p>}
      {valido && catalogo.isLoading && <p className="cargando">Cargando columnas…</p>}
      {valido && datos?.existe === false && (
        <div className="aviso aviso-error">X3 no tiene ninguna tabla {nombre}. Revisa el nombre.</div>
      )}
      {valido && datos && !hay && datos.existe !== false && (
        <div className="aviso">
          {datos.pendiente || pedir.isPending ? (
            <>Leyendo las columnas de {nombre} en X3… Airflow lo hace en su siguiente pasada (menos de un minuto).</>
          ) : puede('dwh.editor') ? (
            <button type="button" className="boton boton-secundario" onClick={() => pedir.mutate()}>
              Leer las columnas de X3
            </button>
          ) : (
            <>Todavía no se han leído las columnas de esta tabla.</>
          )}
        </div>
      )}

      {hay && (
        <>
          <div className="fila-campos casillas">
            <label>
              <input type="radio" name="modo-columnas" checked={todas} onChange={() => alCambiar('*')} /> Todas
              <small className="ayuda"> (también las que se añadan en X3{excluirLob ? '; menos LOB' : ''})</small>
            </label>
            <label>
              <input type="radio" name="modo-columnas" checked={!todas} onChange={() => escribir(actuales())} /> Solo las marcadas
            </label>
          </div>
          {!todas && (
            <div className="filtros">
              <input type="search" placeholder="Buscar por columna o título…" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar columna" />
              <button type="button" className="boton boton-claro" onClick={() => marcarVisibles(true)}>
                Marcar {filtro ? 'las visibles' : 'todas'}
              </button>
              <button type="button" className="boton boton-claro" onClick={() => marcarVisibles(false)}>
                Desmarcar {filtro ? 'las visibles' : 'todas'}
              </button>
              <span className="ayuda">
                {nMarcadas} de {columnas.length} marcadas
              </span>
            </div>
          )}
          <div className="lista-columnas" role="group" aria-label="Columnas de la tabla">
            <table className="tabla">
              <thead>
                <tr>
                  <th />
                  <th>Columna</th>
                  <th>Título en X3</th>
                  <th>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((c) => {
                  const bloqueada = todas || c.obligatoria || !disponible(c);
                  return (
                    <tr key={c.columna} className={disponible(c) ? undefined : 'inactiva'}>
                      <td>
                        <input
                          type="checkbox"
                          checked={marcada(c)}
                          disabled={bloqueada}
                          onChange={() => alternar(c)}
                          aria-label={`${c.columna} ${tituloColumna(c)}`}
                        />
                      </td>
                      <td>
                        <code>{c.columna}</code>
                        {c.obligatoria && (
                          <span className="etiqueta" title="El motor la necesita para la sincronización incremental">
                            siempre
                          </span>
                        )}
                        {c.lob && <span className="etiqueta" title={excluirLob ? 'Excluida por «Excluir LOB»' : 'Texto o binario largo'}>LOB</span>}
                      </td>
                      <td>{tituloColumna(c) || '—'}</td>
                      <td>
                        <small>{c.tipo}</small>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {ajenas.length > 0 && (
            <div className="aviso aviso-error">La configuración tiene columnas que X3 ya no tiene: {ajenas.join(', ')}. Desmárcalas escribiendo la lista a mano.</div>
          )}
          <small className="ayuda">
            Columnas leídas de X3 el {fecha(datos?.actualizado_at)}. Cambiar la selección recarga la tabla.{' '}
            {puede('dwh.editor') && (
              <button type="button" className="enlace" onClick={() => pedir.mutate()} disabled={pedir.isPending || datos?.pendiente}>
                {datos?.pendiente ? 'Releyendo…' : 'Volver a leer'}
              </button>
            )}
          </small>
        </>
      )}

      <details open={aMano || (!hay && !todas)} onToggle={(e) => setAMano((e.target as HTMLDetailsElement).open)}>
        <summary>Escribir la lista a mano</summary>
        <input id="f-columnas" value={valor} onChange={(e) => alCambiar(e.target.value)} aria-label="Lista de columnas" aria-invalid={error ? true : undefined} />
        <small className="ayuda">* = todas, o una lista: ITMREF_0, ITMDES1_0. ROWID y UPDDATTIM_0 van siempre.</small>
      </details>
      {error && (
        <small className="error-campo" role="alert">
          {error}
        </small>
      )}
    </fieldset>
  );
}
